import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _ from 'underscore';
import moment from 'moment';
import { Tooltip } from '../../../../../assets/javascripts/coffee/react/tooltip';

// Normalize a date value from either a string or a Date object into a date object for
// midnight on that date in the browser's time zone
const normalizeDateValue = (value) => {
  if (typeof value === 'string') {
    return moment(value).set({ hours: 0, minutes: 0, seconds: 0 }).toDate();
  }
  return value;
};

const getStateFromProps = ({
  type,
  initialValue,
  initialInputValue,
  initialDateFormat,
  initialTimeFormat,
}) => {
  let initialDisplayValue = null;
  const hasInitialValue = initialValue !== null && initialValue !== undefined;
  let isValid = hasInitialValue;
  let showPreview = hasInitialValue;
  let isBlank = !hasInitialValue;
  let date;

  if (type === 'date') {
    date = normalizeDateValue(initialValue);
    if (!date || date.toString() === 'Invalid Date') {
      date = null;
      isValid = false;
      showPreview = false;
      isBlank = true;
    }
  } else if (!initialValue) {
    date = null;
    isValid = false;
    showPreview = false;
    isBlank = true;
  }

  if (isValid) {
    if (type === 'date') {
      if (Date.compare(date, Date.today()) === 0) {
        initialDisplayValue = 'Today';
      } else if (Date.compare(date, Date.today().add(1).days()) === 0) {
        initialDisplayValue = 'Tomorrow';
      } else {
        initialDisplayValue = date.toString(initialDateFormat);
      }
    } else {
      date = initialValue.toString(initialTimeFormat);
      initialDisplayValue = date;
    }
  }

  return {
    isValid,
    isBlank,
    showPreview,
    currentValue: date,
    value: initialInputValue || initialDisplayValue,
  };
};

const DateInputRow = React.forwardRef(({
  label,
  tooltip,
  tooltipUrl,
  placeholder,
  type = 'date',
  initialValue,
  initialInputValue,
  initialDateFormat = Slzr.Formats.ShortDate,
  initialTimeFormat = Slzr.Formats.LongTime,
  previewDateFormat = Slzr.Formats.LongDate,
  previewTimeFormat = Slzr.Formats.LongTime,
  dateError = 'Unknown date',
  timeError = '',
  errorMessage = '',
  instanceKey = '',
  showPreview: showPreviewProp = true,
  showError = false,
  onChange,
  onEnterKey,
  required = false,
  requiredAsAsterisk = false,
  name,
  wrapperElement: WrapperElement = 'label',
  wrapperClassName = '',
  labelElement: LabelElement = 'span',
  labelClassName = '',
  inputWrapperElement: InputWrapperElement = 'div',
  inputWrapperClassName = 'em-basic-input',
  labelErrorClassName,
  inputErrorClassName,
  inputClassName,
  className,
  errorClassName,
  formHasDateError,
  timeZoneName = '',
}, ref) => {
  const idPrefix = useRef(_.uniqueId('date-input-'));

  const [state, setState] = useState(() => ({
    focused: false,
    ...getStateFromProps({ type, initialValue, initialInputValue, initialDateFormat, initialTimeFormat }),
  }));

  React.useImperativeHandle(ref, () => ({
    state: {
      currentValue: state.currentValue,
      isValid: state.isValid,
      isBlank: state.isBlank,
      value: state.value,
    },
  }), [state.currentValue, state.isValid, state.isBlank, state.value]);

  // Refs for values read inside the prop-change effect but that should not trigger it
  const stateRef = useRef(state);
  stateRef.current = state;
  const prevInstanceKeyRef = useRef(instanceKey);
  const prevInitialValueRef = useRef(initialValue);

  // Reset internal state when instanceKey or initialValue props change
  useEffect(() => {
    const prevIK = prevInstanceKeyRef.current;
    const prevIV = prevInitialValueRef.current;

    let shouldResetState = false;

    if (instanceKey !== prevIK) {
      shouldResetState = true;
    } else if (
      initialValue &&
      !stateRef.current.focused &&
      type === 'date' &&
      initialValue !== prevIV
    ) {
      const newDate = normalizeDateValue(initialValue);
      if (!(stateRef.current.isValid && Date.compare(newDate, stateRef.current.currentValue) === 0)) {
        shouldResetState = true;
      }
    }

    if (shouldResetState) {
      const nextState = getStateFromProps({
        type, initialValue, initialInputValue, initialDateFormat, initialTimeFormat,
      });
      setState((prev) => ({ ...prev, ...nextState }));
    }

    prevInstanceKeyRef.current = instanceKey;
    prevInitialValueRef.current = initialValue;
  }, [instanceKey, initialValue, type, initialInputValue, initialDateFormat, initialTimeFormat]);

  // Format the given Date for the preview
  //
  // date should be a Date representing the parsed input value,
  // or "blank" or "error" for those states
  const formatPreview = useCallback(
    (date) => {
      if (type === 'date') {
        if (date === 'blank') return '';
        if (date === 'error') return dateError;
        return date.toString(previewDateFormat);
      }
      // time
      if (date === 'blank') return '';
      if (date === 'error') return timeError;
      const formatted = date?.toString(previewTimeFormat);
      return timeZoneName ? `${formatted} ${timeZoneName}` : formatted;
    },
    [type, dateError, timeError, previewDateFormat, previewTimeFormat, timeZoneName],
  );

  // Handle change of input
  const handleInputChange = useCallback(
    (evt) => {
      const inputValue = evt.target.value;

      // Parse input into a date or error
      let currentValue;
      if (!inputValue || inputValue.isBlank()) {
        currentValue = 'blank';
      } else {
        const d = Date.parse(inputValue, { bias: 'future_date' });
        currentValue = d instanceof Date ? d : 'error';
      }

      onChange?.(evt, currentValue, inputValue);

      setState((prev) => ({
        ...prev,
        isValid: currentValue !== 'error',
        isBlank: currentValue === 'blank',
        showPreview: currentValue !== 'blank',
        currentValue,
        value: inputValue,
      }));
    },
    [onChange],
  );

  // Handle a key press.
  //
  // If an onEnterKey callback is defined, captures the ENTER keypress and triggers that,
  // otherwise lets it continue.
  //
  // The callback is expected to return true if the keypress should be blocked.
  const handleKeyDown = useCallback(
    (evt) => {
      if (onEnterKey && evt.key === 'Enter') {
        if (onEnterKey(evt)) {
          evt.preventDefault();
        }
      }
    },
    [onEnterKey],
  );

  const handleFocus = useCallback(() => {
    setState((prev) => ({ ...prev, focused: true }));
  }, []);

  const handleBlur = useCallback(() => {
    setState((prev) => ({ ...prev, focused: false }));
  }, []);

  // Build preview element
  let previewEl = null;
  if (showError && errorMessage) {
    previewEl = <div id={`${idPrefix.current}-preview`} className={errorClassName}>{errorMessage}</div>;
  } else if (state.currentValue === 'error' && showError) {
    previewEl = (
      <p id={`${idPrefix.current}-preview`} role="alert" className="preview error">
        {formatPreview(state.currentValue)}
      </p>
    );
  } else if (state.currentValue !== 'blank' && showPreviewProp && state.showPreview) {
    previewEl = (
      <p id={`${idPrefix.current}-preview`} className="preview">
        {formatPreview(state.currentValue)}
      </p>
    );
  }

  // Build label element
  let labelTag = null;
  if (label) {
    const labelClass = showError ? labelErrorClassName : labelClassName;
    const tooltipTag = !_.isBlank(tooltip)
      ? <Tooltip text={tooltip} url={tooltipUrl} />
      : null;

    let decoratedLabel = label;
    if (required && requiredAsAsterisk) {
      decoratedLabel = `${label}*`;
    }

    const requiredTag = required && !requiredAsAsterisk
      ? <sup className="required">REQUIRED</sup>
      : null;

    labelTag = (
      <LabelElement
        htmlFor={LabelElement === 'label' ? name : undefined}
        className={labelClass}
      >
        {decoratedLabel} {requiredTag} {tooltipTag}
      </LabelElement>
    );
  }

  const isInvalid = formHasDateError || (state.currentValue === 'error' && showError);

  // When inputWrapperElement is a React.Fragment, warnings are thrown for the className
  // property. Only include the className property when a class is set and the wrapper
  // component is not a fragment.
  const inputWrapperProps = {};
  if (inputWrapperClassName && InputWrapperElement !== React.Fragment) {
    inputWrapperProps.className = inputWrapperClassName;
  }

  // Combine the theme prop inputClassName with the specified className
  const inputClass = showError ? inputErrorClassName : inputClassName;
  const inputClasses = classNames(inputClass, className);

  return (
    <WrapperElement
      htmlFor={WrapperElement === 'label' ? name : undefined}
      className={wrapperClassName}
    >
      {labelTag}
      <InputWrapperElement {...inputWrapperProps}>
        <input
          type="text"
          value={state.value || ''}
          className={inputClasses}
          aria-describedby={previewEl ? `${idPrefix.current}-preview` : undefined}
          aria-invalid={isInvalid}
          name={name}
          id={name}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={placeholder}
        />
      </InputWrapperElement>
      {previewEl}
    </WrapperElement>
  );
});

DateInputRow.displayName = 'DateInputRow';

DateInputRow.propTypes = {
  label: PropTypes.string,
  tooltip: PropTypes.string,
  tooltipUrl: PropTypes.string,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['time', 'date']).isRequired,
  initialValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
  ]),
  initialInputValue: PropTypes.string,
  onChange: PropTypes.func,
  instanceKey: PropTypes.any,
  required: PropTypes.bool,
  requiredAsAsterisk: PropTypes.bool,
  name: PropTypes.string,
  showPreview: PropTypes.bool,
  showError: PropTypes.bool,
  initialDateFormat: PropTypes.string,
  initialTimeFormat: PropTypes.string,
  previewDateFormat: PropTypes.string,
  previewTimeFormat: PropTypes.string,
  dateError: PropTypes.string,
  timeError: PropTypes.string,
  errorMessage: PropTypes.string,
  onEnterKey: PropTypes.func,
  formHasDateError: PropTypes.bool,
  errorClassName: PropTypes.string,
  labelErrorClassName: PropTypes.string,
  inputErrorClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  className: PropTypes.string,
  timeZoneName: PropTypes.string,

  // Theme-related props
  wrapperElement: PropTypes.elementType.isRequired,
  wrapperClassName: PropTypes.string,
  labelElement: PropTypes.elementType.isRequired,
  labelClassName: PropTypes.string,
  inputWrapperElement: PropTypes.elementType.isRequired,
  inputWrapperClassName: PropTypes.string,
};

DateInputRow.defaultProps = {
  inputWrapperElement: 'div',
  wrapperElement: 'label',
  labelElement: 'span',
  type: 'date',
}

export { DateInputRow };
